import React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Trans } from '@lingui/react';
import { i18n } from '@lingui/core';
import { NominalOptimisationParams } from '../../../MineModel/types';
import PCPipe from '../../../MineModel/Pipes/PCPipe';

interface NominalParametersSectionProps {
  value: NominalOptimisationParams;
  onChange: (params: NominalOptimisationParams) => void;
  pipes: Record<string, PCPipe>;
  disabled: boolean;
}

const NominalParametersSection = ({ value, onChange, pipes, disabled }: NominalParametersSectionProps) => {
  const pipeOptions = React.useMemo(() => Object.values(pipes).map((pipe) => ({
    id: pipe.pipeId,
    label: pipe.name || `Pipe ${pipe.pipeId}`,
  })), [pipes]);

  const selectedExcludedPipes = React.useMemo(
    () => pipeOptions.filter((p) => value.excludedPipeIds?.includes(p.id)),
    [pipeOptions, value.excludedPipeIds],
  );

  const handleNumberChange = (field: keyof NominalOptimisationParams) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
    onChange({ ...value, [field]: val });
  };

  const handleExcludedPipesChange = (_event: React.SyntheticEvent, newValue: { id: string; label: string }[]) => {
    onChange({ ...value, excludedPipeIds: newValue.map((p) => p.id) });
  };

  return (
    <Accordion defaultExpanded={false} disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none' }, mt: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 'unset' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          <Trans id="Optional Parameters">Optional Parameters</Trans>
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            disabled={disabled}
            size="small"
            type="number"
            fullWidth
            color="secondary"
            value={value.minYield ?? ''}
            onChange={handleNumberChange('minYield')}
            label={i18n._('Min Yield Stress (Slack Line)')}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">Pa</InputAdornment>,
              },
            }}
          />
          <TextField
            disabled={disabled}
            size="small"
            type="number"
            fullWidth
            color="secondary"
            value={value.maxYield ?? ''}
            onChange={handleNumberChange('maxYield')}
            label={i18n._('Max Yield Stress (Optimised Line)')}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">Pa</InputAdornment>,
              },
            }}
          />
          <TextField
            disabled={disabled}
            size="small"
            type="number"
            fullWidth
            color="secondary"
            value={value.pipeRatingOffsetPercent ?? ''}
            onChange={handleNumberChange('pipeRatingOffsetPercent')}
            label={i18n._('Pipe Rating Offset')}
            helperText={i18n._('Percentage of pump pressure added to grade line')}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              },
            }}
          />
        </Box>
        {(value.pipeRatingOffsetPercent !== undefined && value.pipeRatingOffsetPercent > 0) && (
          <Box sx={{ pt: 2 }}>
            <Autocomplete
              multiple
              disabled={disabled}
              size="small"
              options={pipeOptions}
              value={selectedExcludedPipes}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              onChange={handleExcludedPipesChange}
              disableCloseOnSelect
              color="secondary"
              renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.id} label={option.label} size="small" />
              ))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={i18n._('Exclude Pipes from Offset Check')}
                  placeholder={i18n._('Select pipes to exclude')}
                  color="secondary"
                />
              )}
            />
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default NominalParametersSection;
